import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  FindCertificatesQueryResponse,
  FindCertificatesQueryParams,
} from "../types/FindCertificates"

type FindCertificatesClient = typeof client<
  FindCertificatesQueryResponse,
  Error,
  never
>
type FindCertificates = {
  data: FindCertificatesQueryResponse
  error: Error
  request: never
  pathParams: never
  queryParams: FindCertificatesQueryParams
  headerParams: never
  response: Awaited<ReturnType<FindCertificatesClient>>
  client: {
    parameters: Partial<Parameters<FindCertificatesClient>[0]>
    return: Awaited<ReturnType<FindCertificatesClient>>
  }
}
export const findCertificatesQueryKey = (
  params: FindCertificates["queryParams"]
) =>
  [
    { url: "/v1/certificate/find-certificates" },
    ...(params ? [params] : []),
  ] as const
export type FindCertificatesQueryKey = ReturnType<
  typeof findCertificatesQueryKey
>
export function findCertificatesQueryOptions<
  TData = FindCertificates["response"],
  TQueryData = FindCertificates["response"],
>(
  params: FindCertificates["queryParams"],
  options: FindCertificates["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindCertificates["response"],
    FindCertificates["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findCertificatesQueryKey(params)
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        FindCertificates["data"],
        FindCertificates["error"]
      >({
        method: "get",
        url: `/v1/certificate/find-certificates`,
        params,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Find certificates
 * @link /v1/certificate/find-certificates
 */
export function useFindCertificates<
  TData = FindCertificates["response"],
  TQueryData = FindCertificates["response"],
  TQueryKey extends QueryKey = FindCertificatesQueryKey,
>(
  params: FindCertificates["queryParams"],
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindCertificates["response"],
        FindCertificates["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindCertificates["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindCertificates["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? findCertificatesQueryKey(params)
  const query = useQuery<
    FindCertificates["data"],
    FindCertificates["error"],
    TData,
    any
  >({
    ...findCertificatesQueryOptions<TData, TQueryData>(params, clientOptions),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindCertificates["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
